import { useCallback, useMemo, useSyncExternalStore } from 'react';

import {
  array,
  flow,
  nonEmptyArray,
  not,
  option,
  panic,
  pipe,
  remote,
  task,
} from '@code-expert/prelude';
import type { UserId } from '/imports/domain';
import { invokeQuery } from '/imports/ui/hooks/useProcedure';

export interface UserNameInterface {
  _id: UserId;
  userName: string;
  email: string;
  userTitleName: string;
}

/**
 * In-memory cache of username data
 */
const userNameStore = (() => {
  let store = new Map<UserId, remote.Remote<UserNameInterface>>();
  const listeners = new Set<VoidFunction>();
  const subscribe = (listener: VoidFunction): VoidFunction => {
    listeners.add(listener);
    return () => void listeners.delete(listener);
  };
  const setUserName = (key: UserId, value: remote.Remote<UserNameInterface>) => {
    if (store.get(key) !== value) {
      // React requires an immutable store. We indicate mutations using a shallow copy.
      store = new Map(store.set(key, value));
    }
    listeners.forEach((x) => x());
  };
  return {
    subscribe,
    setUserName,
    exists: (id: UserId) => store.has(id),
    getSnapshot: () => store,
  };
})();

/**
 * Hook to improve performance of getting the userName by using an in-memory cache
 */
export const useUserNameCache = () => {
  const cache = useSyncExternalStore(userNameStore.subscribe, userNameStore.getSnapshot);

  /**
   * Manages fetching usernames and stores the result in the cache
   */
  const fillCache = useCallback(
    (userIds: Array<UserId>): void => {
      const updateCache = (usernames: Array<UserNameInterface>): void =>
        usernames.forEach((u) => userNameStore.setUserName(u._id, remote.done(u)));

      void pipe(
        userIds,
        array.filter(not(userNameStore.exists)),
        nonEmptyArray.fromArray,
        option.traverse(task.ApplicativePar)(
          flow(
            nonEmptyArray.map((id) => (userNameStore.setUserName(id, remote.pending), id)),
            invokeQuery('user_getNames'),
            task.chainFirstVoid(updateCache),
          ),
        ),
        task.run,
      );
    },
    [cache],
  );

  const makeLookup =
    (userIds: Array<UserId>) =>
    (id: UserId): remote.Remote<UserNameInterface> => {
      fillCache(userIds);
      return cache.get(id) ?? remote.pending;
    };

  /**
   * Find all usernames for a list of user IDs
   */
  const getUsersFromCache = useCallback(
    (userIds: Array<UserId>) =>
      pipe(userIds, array.traverse(remote.Applicative)(makeLookup(userIds))),
    [fillCache],
  );

  /**
   * Find the username for a specific user ID
   */
  const getUserFromCache = useCallback(
    (userId: UserId) =>
      pipe(
        getUsersFromCache([userId]),
        remote.map(
          flow(
            array.head,
            option.getOrElseW(() => panic('Failed to lookup username', { userId })),
          ),
        ),
      ),
    [getUsersFromCache],
  );

  return useMemo(
    () => ({ getUserFromCache, getUsersFromCache, fillCache }),
    [getUserFromCache, getUsersFromCache, fillCache],
  );
};
